import CustomButton from '@/common/CustomButton';
import Layout from '@/common/Layout';
import CustomTable from '@/common/Table';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { DatePicker, Tag } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { columnRenderer, headerRenderer } from 'utilities/helpers';
import { getPlans, selectTaskSlice } from '../redux/slice';
import { format } from 'date-fns';

const RECORDS_PER_PAGE = 10;

export default function Plans() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [plans, setPlans] = useState({ data: [], pagination: {} });
  const dispatch = useDispatch();
  const { loading } = useSelector(selectTaskSlice);

  const handleGetPlans = (date, page = 1, per_page = RECORDS_PER_PAGE) => {
    const payload = {};
    if (date) {
      payload.plan_date = date;
    } else {
      payload.page = page;
      payload.per_page = per_page;
    }
    dispatch(getPlans(payload)).then((res) => {
      if (res?.payload?.success) {
        const data = res.payload.task_summary;
        if (Array.isArray(data)) {
          setPlans({
            data,
            pagination: {
              current: res?.payload?.pagination.current_page,
              pageSize: RECORDS_PER_PAGE,
              total: res?.payload?.pagination.total_records
            }
          });
        } else {
          setPlans({ data: [data] });
        }
      }
    });
  };

  useEffect(() => {
    handleGetPlans(selectedDate);
  }, [selectedDate]);

  const columns = [
    {
      title: headerRenderer('Date'),
      dataIndex: 'date',
      key: 'date',
      width: '20%',
      render: (text) =>
        columnRenderer(
          text ? format(text, 'MMMM dd, yyyy') : '',
          '!text-[#0653fa]'
        )
    },
    {
      title: headerRenderer('Tasks'),
      dataIndex: 'tasks',
      key: 'tasks',
      width: '15%',
      render: (text) => columnRenderer(text, '!text-gray-500')
    },
    {
      title: headerRenderer('Completed'),
      dataIndex: 'completed',
      key: 'completed',
      width: '15%',
      render: (text) => columnRenderer(text, '!text-gray-500')
    },
    {
      title: headerRenderer('Completion'),
      dataIndex: 'completion',
      key: 'completion',
      width: '20%',
      render: (text) => {
        const getColorForPercentage = (value) => {
          if (value <= 50) return '#f87171 ';
          if (value <= 80) return '#facc15 ';
          if (value <= 99) return '#60a5fa  ';
          return '#4ade80 ';
        };
        const color = getColorForPercentage(text);

        return (
          <div className="flex items-center space-x-2">
            <div className="w-32 h-2 overflow-hidden bg-gray-200 rounded-full">
              <div
                className="h-2 rounded-full"
                style={{ width: text + '%', backgroundColor: color }}
              ></div>
            </div>

            <span className="text-xs text-gray-500">{text}%</span>
          </div>
        );
      }
    },
    {
      title: headerRenderer('Status'),
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      render: (text) =>
        text === 'published' ? (
          <Tag
            style={{ fontFamily: 'SFProText Regular' }}
            className="inline-block px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50"
          >
            Published
          </Tag>
        ) : (
          <Tag
            style={{ fontFamily: 'SFProText Regular' }}
            className="inline-block px-3 py-1 text-xs font-medium text-yellow-700 bg-yellow-50"
          >
            Draft
          </Tag>
        )
    },
    {
      title: headerRenderer('Action'),
      dataIndex: 'view',
      key: 'view',
      width: '10%',
      render: (text, record) =>
        text ? (
          <Link
            to={`/view-plan?date=${text}`}
            className="!font-bold !text-xs"
            style={{
              fontFamily: 'SFProText Bold'
            }}
          >
            View Plan
          </Link>
        ) : null
    }
  ];

  const dataSource = plans.data.length
    ? plans.data
        .filter((plan) => plan.status === 'published')
        .map((plan) => {
          return {
            date: plan.date,
            tasks: plan.total_tasks,
            completed: plan.completed_tasks,
            completion: plan.completion_percentage,
            status: plan.status,
            view: plan.date
          };
        })
    : [];

  const handleDateChange = (_, date) => {
    setSelectedDate(date);
  };

  const handleTableChange = (newPagination) => {
    handleGetPlans('', newPagination.current, newPagination.pageSize);
  };

  return (
    <Layout sider={false}>
      <div className="flex flex-col gap-6 pb-6">
        {/* header */}
        <div className="flex items-center justify-between gap-1">
          <label
            className={'!text-xl !m-0 md:!text-3xl !font-bold md:mt-0 mt-2'}
          >
            Plans
          </label>

          <div className="flex items-center gap-4">
            <DatePicker
              placeholder="Select Date"
              placement="bottomRight"
              onChange={handleDateChange}
            />

            <CustomButton type="outlined" onClick={() => navigate('/tasks')}>
              <ArrowLeftOutlined /> Back to Planning
            </CustomButton>
          </div>
        </div>

        <CustomTable
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          onChange={handleTableChange}
          pagination={plans?.pagination}
          emptyText={'No plans to show'}
        />
      </div>
    </Layout>
  );
}
